import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'sonner';

interface VerificationResult {
  success: boolean;
  aRecordValid: boolean;
  txtRecordValid: boolean;
  errors: string[];
  details: {
    expectedARecord: string;
    expectedTxtRecord: string;
    foundARecords: string[];
    foundTxtRecords: string[];
  };
}

export interface EnterpriseBranding {
  id: string;
  userId: string;
  customDomain?: string;
  domainVerified: boolean;
  domainVerificationToken?: string;
  domainVerifiedAt?: string;
  companyName?: string;
  companyLogoUrl?: string;
  faviconUrl?: string;
  redirectPageLogoUrl?: string;
  removePoweredBy: boolean;
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  custom404Message?: string;
  customExpiredMessage?: string;
  emailFromName?: string;
  emailHeaderLogoUrl?: string;
  emailFooterText?: string;
  lastVerificationCheckAt?: string;
  verificationCheckStatus?: string;
  verificationErrors?: string[];
  createdAt: string;
  updatedAt: string;
}

export { type VerificationResult };

interface BrandingUpdate {
  customDomain?: string;
  companyName?: string;
  companyLogoUrl?: string;
  faviconUrl?: string;
  redirectPageLogoUrl?: string;
  removePoweredBy?: boolean;
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
  custom404Message?: string;
  customExpiredMessage?: string;
  emailFromName?: string;
  emailHeaderLogoUrl?: string;
  emailFooterText?: string;
}

function mapDBToBranding(db: Record<string, unknown>): EnterpriseBranding {
  return {
    id: db.id as string,
    userId: db.user_id as string,
    customDomain: db.custom_domain as string | undefined,
    domainVerified: db.domain_verified as boolean,
    domainVerificationToken: db.domain_verification_token as string | undefined,
    domainVerifiedAt: db.domain_verified_at as string | undefined,
    companyName: db.company_name as string | undefined,
    companyLogoUrl: db.company_logo_url as string | undefined,
    faviconUrl: db.favicon_url as string | undefined,
    redirectPageLogoUrl: db.redirect_page_logo_url as string | undefined,
    removePoweredBy: db.remove_powered_by as boolean,
    primaryColor: db.primary_color as string,
    secondaryColor: db.secondary_color as string,
    backgroundColor: db.background_color as string,
    textColor: db.text_color as string,
    custom404Message: db.custom_404_message as string | undefined,
    customExpiredMessage: db.custom_expired_message as string | undefined,
    emailFromName: db.email_from_name as string | undefined,
    emailHeaderLogoUrl: db.email_header_logo_url as string | undefined,
    emailFooterText: db.email_footer_text as string | undefined,
    lastVerificationCheckAt: db.last_verification_check_at as string | undefined,
    verificationCheckStatus: db.verification_check_status as string | undefined,
    verificationErrors: db.verification_errors as string[] | undefined,
    createdAt: db.created_at as string,
    updatedAt: db.updated_at as string,
  };
}

function generateVerificationToken(): string {
  return `nexqr_verify_${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}`;
}

export function useEnterpriseBranding() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: branding, isLoading } = useQuery({
    queryKey: ['enterprise-branding', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('enterprise_branding')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data ? mapDBToBranding(data as Record<string, unknown>) : null;
    },
    enabled: !!user,
  });

  const saveBranding = async (updates: BrandingUpdate): Promise<EnterpriseBranding> => {
    if (!user) throw new Error('Not authenticated');

    const dbUpdates: Record<string, unknown> = {};
    
    if (updates.customDomain !== undefined) dbUpdates.custom_domain = updates.customDomain;
    if (updates.companyName !== undefined) dbUpdates.company_name = updates.companyName;
    if (updates.companyLogoUrl !== undefined) dbUpdates.company_logo_url = updates.companyLogoUrl;
    if (updates.faviconUrl !== undefined) dbUpdates.favicon_url = updates.faviconUrl;
    if (updates.redirectPageLogoUrl !== undefined) dbUpdates.redirect_page_logo_url = updates.redirectPageLogoUrl;
    if (updates.removePoweredBy !== undefined) dbUpdates.remove_powered_by = updates.removePoweredBy;
    if (updates.primaryColor !== undefined) dbUpdates.primary_color = updates.primaryColor;
    if (updates.secondaryColor !== undefined) dbUpdates.secondary_color = updates.secondaryColor;
    if (updates.backgroundColor !== undefined) dbUpdates.background_color = updates.backgroundColor;
    if (updates.textColor !== undefined) dbUpdates.text_color = updates.textColor;
    if (updates.custom404Message !== undefined) dbUpdates.custom_404_message = updates.custom404Message;
    if (updates.customExpiredMessage !== undefined) dbUpdates.custom_expired_message = updates.customExpiredMessage;
    if (updates.emailFromName !== undefined) dbUpdates.email_from_name = updates.emailFromName;
    if (updates.emailHeaderLogoUrl !== undefined) dbUpdates.email_header_logo_url = updates.emailHeaderLogoUrl;
    if (updates.emailFooterText !== undefined) dbUpdates.email_footer_text = updates.emailFooterText;

    if (branding) {
      // Update existing
      const { data, error } = await supabase
        .from('enterprise_branding')
        .update(dbUpdates)
        .eq('id', branding.id)
        .select()
        .single();

      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['enterprise-branding'] });
      toast.success('Branding settings saved!');
      return mapDBToBranding(data as Record<string, unknown>);
    } else {
      // Create new with verification token
      const { data, error } = await supabase
        .from('enterprise_branding')
        .insert({
          user_id: user.id,
          domain_verification_token: generateVerificationToken(),
          ...dbUpdates,
        })
        .select()
        .single();

      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ['enterprise-branding'] });
      toast.success('Branding settings created!');
      return mapDBToBranding(data as Record<string, unknown>);
    }
  };

  const setCustomDomain = async (domain: string) => {
    if (!user) throw new Error('Not authenticated');

    const cleanDomain = domain.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
    const token = generateVerificationToken();

    if (branding) {
      const { error } = await supabase
        .from('enterprise_branding')
        .update({
          custom_domain: cleanDomain,
          domain_verified: false,
          domain_verification_token: token,
          domain_verified_at: null,
        })
        .eq('id', branding.id);

      if (error) throw error;
    } else {
      const { error } = await supabase
        .from('enterprise_branding')
        .insert({
          user_id: user.id,
          custom_domain: cleanDomain,
          domain_verification_token: token,
        });

      if (error) throw error;
    }

    queryClient.invalidateQueries({ queryKey: ['enterprise-branding'] });
    toast.success('Domain added! Please configure DNS records.');
  };

  const verifyDomain = async (): Promise<VerificationResult> => {
    if (!branding?.customDomain) {
      toast.error('No domain to verify');
      return {
        success: false,
        aRecordValid: false,
        txtRecordValid: false,
        errors: ['No domain configured'],
        details: {
          expectedARecord: '185.158.133.1',
          expectedTxtRecord: '',
          foundARecords: [],
          foundTxtRecords: [],
        },
      };
    }

    toast.info('Checking DNS records...');

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please sign in to verify domain');
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-domain`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.session.access_token}`,
          },
        }
      );

      const result: VerificationResult = await response.json();

      if (!response.ok) {
        const errorMsg = (result as unknown as { error?: string }).error || 'Verification failed';
        toast.error(errorMsg);
        throw new Error(errorMsg);
      }

      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['enterprise-branding'] });
        toast.success('Domain verified successfully!');
      } else {
        result.errors.forEach(err => toast.error(err));
      }

      return result;
    } catch (error) {
      console.error('Domain verification error:', error);
      return {
        success: false,
        aRecordValid: false,
        txtRecordValid: false,
        errors: [(error as Error).message || 'Verification failed'],
        details: {
          expectedARecord: '185.158.133.1',
          expectedTxtRecord: branding.domainVerificationToken || '',
          foundARecords: [],
          foundTxtRecords: [],
        },
      };
    }
  };

  const removeDomain = async () => {
    if (!branding) return;

    const { error } = await supabase
      .from('enterprise_branding')
      .update({
        custom_domain: null,
        domain_verified: false,
        domain_verification_token: null,
        domain_verified_at: null,
      })
      .eq('id', branding.id);

    if (error) throw error;
    queryClient.invalidateQueries({ queryKey: ['enterprise-branding'] });
    toast.success('Domain removed');
  };

  const uploadAsset = async (file: File, type: 'logo' | 'favicon' | 'redirect-logo' | 'email-logo'): Promise<string> => {
    if (!user) throw new Error('Not authenticated');

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${type}-${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('branding-assets')
      .upload(fileName, file, { upsert: true });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('branding-assets')
      .getPublicUrl(fileName);

    return publicUrl;
  };

  return {
    branding,
    isLoading,
    saveBranding,
    setCustomDomain,
    verifyDomain,
    removeDomain,
    uploadAsset,
  };
}
